import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { IQuestionComponent } from 'app/interfaces/IQuestionComponent'
import { TranslocoService } from '@ngneat/transloco'
import { SurveyViewerService } from 'app/modules/survey/survey-viewer/services/survey-viewer.service'
import { AbstractControl } from '@angular/forms'
import { OpenStatementErrorMatcher } from 'app/error-matchers/OpenStatementErrorMatcher'
import { IImageSelection } from 'app/interfaces/IImageSelection'
import { Image } from 'app/models/questions/Image'
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog'
import { ImageModalComponent } from 'app/modules/survey/survey-viewer/image-modal/image-modal.component'
import {BehaviorSubject, Subject, takeUntil} from "rxjs";
import {IValidationResponse, ValidateCommentsAIService} from "@store/validateCommentsAI.service";
import {InputPersistenceService} from '../../../services/input-persistence.service';

@Component({
  selector: 'app-image',
  templateUrl: './image.component.html',
  styleUrls: ['./image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageComponent implements IQuestionComponent, OnInit, OnDestroy {

  @Input() question: Image
  @Input() isSmallScreen: boolean
  @Input() activeLang: string

  questionFormControl: AbstractControl
  errorMatcher: OpenStatementErrorMatcher
  isValidAI: Record<string, Record<string, BehaviorSubject<IValidationResponse>>> = {};
  private ngUnsubscribe = new Subject<void>();


  constructor(
    public translocoService: TranslocoService,
    public surveyViewerService: SurveyViewerService,
    private validateAI: ValidateCommentsAIService,
    private dialog: MatDialog,
    private inputPersistence: InputPersistenceService
  ) { }

  ngOnInit(): void {
    this.errorMatcher = new OpenStatementErrorMatcher(this.question.isOptional)
    this.questionFormControl = this.surveyViewerService.activeSurvey$.value.form.get(this.question.key)
    this.question.rowKeys.forEach(rKey => {
      this.question.statementKeys.forEach(sKey => {
        const commentsControl = this.questionFormControl.get(rKey).get(this.question.commentsControlName).get(sKey);
        if (commentsControl){
          this.surveyViewerService.changeFormUpdateStrategy(commentsControl, 'blur');
          // Restaurer la valeur sauvegardée si elle existe (priorité à la valeur en cours de saisie)
          const savedValue = this.inputPersistence.getValue(this.question.key, sKey, rKey);
          if (savedValue) {
            commentsControl.setValue(savedValue, {emitEvent: false});
          }
        }
      })
    });

    // Surveiller les changements du Survey pour restaurer les valeurs quand le formulaire se recharge
    this.surveyViewerService.activeSurvey$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(survey => {
      if (survey) {
        this.question.rowKeys.forEach(rKey => {
          this.question.statementKeys.forEach(sKey => {
            const newCommentsControl = survey.form
              .get(this.question.key)
              ?.get(rKey)
              ?.get(this.question.commentsControlName)
              ?.get(sKey);
            
            if (newCommentsControl) {
              const currentValue = newCommentsControl.value;
              const savedValue = this.inputPersistence.getValue(this.question.key, sKey, rKey);
              
              if (savedValue && (!currentValue || currentValue === '')) {
                setTimeout(() => {
                  newCommentsControl.setValue(savedValue, {emitEvent: false});
                }, 0);
              }
            }
          });
        });
      }
    });
  }

  displayLastImageSelectionLabel(imageSelection?: IImageSelection): boolean {
    if (!imageSelection || !imageSelection.selectLast) {
      return false
    }

    return this.question.getSelectedStatementsCount(this.questionFormControl) === this.question.rowKeys.length
  }

  disableImageSelection(sKey, questionForm: AbstractControl): boolean {
    const rowKey = this.question.findRowKeyOfSelectedImage(sKey, questionForm)
    const rowHasNoSelection = !questionForm.get(rowKey)
    return (this.question.getSelectedStatementsCount(questionForm) === this.question.rowKeys.length) && rowHasNoSelection
  }

  displayImageSelectionLabel(imageSelection?: IImageSelection): boolean {
    if (!imageSelection || !imageSelection.selectLabel) {
      return false
    }

    return !(imageSelection.selectLast && this.question.getSelectedStatementsCount(this.questionFormControl) === this.question.rowKeys.length)
  }

  buildImageUrlWithoutWidthParameter(url: string): string {
    return url.split('?w=')[0]
  }

  displayRowLabel(sKey, form): Record<string, string> {
    return !this.question.findRowKeyOfSelectedImage(sKey, form) ? undefined :
      this.question.rows[this.question.findRowKeyOfSelectedImage(sKey, form)].label
  }

  selectImage(sKey, questionFormControl, event: MouseEvent): boolean {
    const paths = event.composedPath() as any[]
    for (const pathsKey in paths) {
      if (paths[pathsKey].dataset && paths[pathsKey].dataset['prevent']) {
        return false
      }
    }
    this.question.selectImage(sKey, questionFormControl)
    return true
  }

  openImageDialog(imgUrl: string) {
    this.dialog.open(ImageModalComponent, {
      data: imgUrl,
      width: '100%',
      maxHeight: '90vh'
    })
  }

  onBlur(value: string, sKey: string, rowKey: string){
    this.questionFormControl.get(rowKey).get(this.question.commentsControlName).get(sKey).setValue(value)
    // Note: On ne nettoie PAS ici car le formulaire peut se recharger juste après

    this.isValidAI[rowKey] ??= {};
    this.isValidAI[rowKey][sKey] ??= new BehaviorSubject<IValidationResponse>({isValid: true});
    if (!this.question.disableOpenStatementsRealtimeValidation) {
      this.validateAI.validateAnswerWithOpenAI(this.question.question[this.activeLang], value, this.activeLang)
        .then((response) => {
          this.isValidAI[rowKey][sKey].next(response);
          this.updateQuestionAIStatus();
        });
    }
  }

  /**
   * Sauvegarde la valeur en temps réel (appelé à chaque frappe)
   */
  onInput(value: string, sKey: string, rowKey: string){
    this.inputPersistence.saveValue(this.question.key, value, sKey, rowKey);
  }

  private updateQuestionAIStatus() {
    const hasError = Object.values(this.isValidAI).some(row =>
      Object.values(row).some(subject => !subject.value.isValid)
    );
    this.validateAI.updateQuestionAIValidationStatus(this.question.key, !hasError);
  }

  ngOnDestroy(){
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

}
